<template>
  <div class="page">
    <div class="layout">
      <div v-if="!isMovement" class="banners-container">
        <BannersCarousel />
      </div>
      <div class="layout__item layout__item_swap">
        <Transition
          mode="in-out"
          name="fade"
          :duration="200"
          @before-enter="onBeforeEnter"
          @after-enter="onAfterAppear"
          @before-leave="onBeforeLeave"
          @after-leave="onAfterLeave"
        >
          <div v-if="isPriceChartShown" class="chart-container">
            <PriceChart class="chart" />
          </div>
        </Transition>
        <SwapContainer :class="className" class="swap">
          <template #swappingRoute>
            <SwappingRoute />
          </template>
        </SwapContainer>
      </div>
    </div>
  </div>

  <AuditsInfo />
</template>

<script setup lang="ts">
import { AuditsInfo } from '@/components/UI/AuditsInfo';
import { BannersCarousel, SwapContainer } from '@/components/Swap';
import PriceChart from '@/components/PriceChart/PriceChart.vue';
import { usePriceChart } from '@/composables/usePriceChart';
import { computed, ref } from 'vue';
import SwappingRoute from '@/components/Swap/SwappingRoute.vue';
import { useWindowSize } from '@vueuse/core';
import { ChainsEnum, CORRECT_CHAIN } from '@/constants';

const { isPriceChartShown } = usePriceChart();
const { width } = useWindowSize();

const className = ref('');

const isMobile = computed(() => width.value <= 1024);

const isMovement = computed(() =>
  [ChainsEnum['movement-testnet'], ChainsEnum['movement-mainnet']].includes(
    CORRECT_CHAIN.id,
  ),
);

const showInSwapContainer = computed(() => {
  return (
    (isPriceChartShown.value && isMobile.value) || !isPriceChartShown.value
  );
});

function onBeforeEnter() {
  if (!isMobile.value) {
    className.value = 'slide-on';
  }
}

function onAfterAppear() {
  className.value = '';
}

function onBeforeLeave() {
  className.value = 'slide-off';
}

function onAfterLeave() {
  if (!isMobile.value) {
    setTimeout(() => {
      requestAnimationFrame(() => {
        className.value = '';
      });
    }, 200);
  }
}
</script>

<style scoped lang="scss">
.layout {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  transition: width 0.2s ease;
  gap: 1rem;

  &__item {
    display: flex;
    position: relative;
    row-gap: 0.5rem;
    column-gap: 1rem;
  }

  &__item_swap {
    height: 100%;
    width: 100%;

    @media (max-width: 1024px) {
      flex-direction: column-reverse;
    }
  }
}

.page {
  display: flex;
  justify-content: center;
  width: 100%;
  gap: 8px;
  max-width: 1338px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (max-width: 1024px) {
    max-width: 960px;
    flex-direction: column-reverse;
  }

  @media (max-width: 500px) {
    max-width: 100dvw;
  }

  @media (max-width: 360px) {
    padding: 0 8px;
  }
}
.swap {
  display: flex;
  justify-content: space-between;
  height: 100%;
  position: relative;
  flex: 1;
  margin: 0 auto;
  width: 100%;
  transition: all 0.2s ease-in;
}

.chart-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.5rem;
  width: 640px;

  .chart {
    max-height: 502px;
  }

  @media (max-width: 1300px) {
    width: 580px;
    .chart {
      height: 375px;
    }
  }

  @media (max-width: 1200px) {
    width: 520px;
    .chart {
      height: 375px;
    }
  }

  @media (max-width: 1100px) {
    width: 480px;
    .chart {
      height: 375px;
    }
  }

  @media (max-width: 1024px) {
    min-height: 480px;
    width: 480px;
    flex: initial;
    margin: 0 auto;
    .chart {
      max-height: unset;
      width: 480px;
      margin: 0 auto;
    }
  }

  @media (max-width: 500px) {
    width: 100%;
    .chart {
      width: 100%;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 200ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media only screen and (max-width: 768px) {
  .slide-on {
    animation: slide-on 200ms ease-in-out;
    transform: translateX(2px);
  }
}
.slide-off {
  transition: all 200ms ease 200ms;
}

@keyframes slide-on {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(0%);
  }
}

@keyframes slide-off {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-10%);
  }
}

.dashboard-banner {
  max-width: 480px;
}

.dashboard-content {
  @media screen and (max-width: 1024px) {
    font-size: 15px;
  }

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
}

.pool-banner-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0.5rem;

  @media screen and (min-width: 1024px) {
    width: 300px;
  }

  @media screen and (max-width: 1024px) {
    padding: 0 0.5rem 0 1rem;
  }

  @media screen and (max-width: 350px) {
    padding: 0;
  }
}

.call-to-action-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.4399999976158142px;
  text-align: center;

  min-width: 100px;

  @media screen and (max-width: 768px) {
    font-size: 10px;
    min-width: 60px;
  }

  @media screen and (max-width: 350px) {
    font-size: 8px;
    min-width: 55px;
  }
}

.banners-container {
  width: 480px;
  @media (max-width: 431px) {
    max-width: 100%;
  }
}
</style>
